import { supabase } from '../lib/supabase';
import OpenAI from 'openai';

const openai = new OpenAI({
  apiKey: import.meta.env.VITE_OPENAI_API_KEY,
  dangerouslyAllowBrowser: true,
});

function getErrorMessage(error) {
  if (error instanceof OpenAI?.AuthenticationError) {
    return { isInternal: true, message: 'Invalid API key or authentication failed. Please check your OpenAI API key.' };
  } else if (error instanceof OpenAI?.PermissionDeniedError) {
    return { isInternal: true, message: 'Quota exceeded or authorization failed. You may have exceeded your usage limits.' };
  } else if (error instanceof OpenAI?.RateLimitError) {
    return { isInternal: true, message: 'Rate limit exceeded. Please wait a moment and try again.' };
  } else if (error instanceof OpenAI?.InternalServerError) {
    return { isInternal: true, message: 'OpenAI service is currently unavailable. Please try again later.' };
  } else if (error instanceof OpenAI?.APIConnectionError) {
    return { isInternal: true, message: 'Unable to connect to OpenAI service. Please check your internet connection.' };
  } else {
    return { isInternal: false, message: error?.message || 'An unexpected error occurred. Please try again.' };
  }
}

export const supportTicketService = {
  /**
   * Analyze ticket content with AI for priority and category classification
   */
  async analyzeTicket(subject, description) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: `You are an AI support ticket analyzer for a gym management system. Analyze support tickets and provide:
1. Priority level (low, medium, high, urgent)
2. Category (billing, membership, technical, facility, class_booking, general)
3. Sentiment (positive, neutral, negative, frustrated)
4. Suggested department (billing, operations, technical_support, member_services)
5. Complexity score (1-10)
6. Requires human intervention (true/false)
7. Brief analysis summary`
          },
          {
            role: 'user',
            content: `Subject: ${subject}\n\nDescription: ${description}`
          }
        ],
        response_format: {
          type: 'json_schema',
          json_schema: {
            name: 'ticket_analysis',
            schema: {
              type: 'object',
              properties: {
                priority: { type: 'string', enum: ['low', 'medium', 'high', 'urgent'] },
                category: { type: 'string', enum: ['billing', 'membership', 'technical', 'facility', 'class_booking', 'general'] },
                sentiment: { type: 'string', enum: ['positive', 'neutral', 'negative', 'frustrated'] },
                suggested_department: { type: 'string', enum: ['billing', 'operations', 'technical_support', 'member_services'] },
                complexity_score: { type: 'number', minimum: 1, maximum: 10 },
                requires_human: { type: 'boolean' },
                analysis_summary: { type: 'string' }
              },
              required: ['priority', 'category', 'sentiment', 'suggested_department', 'complexity_score', 'requires_human', 'analysis_summary'],
              additionalProperties: false
            }
          }
        },
        reasoning_effort: 'medium',
        verbosity: 'low'
      });

      const analysis = JSON.parse(response?.choices?.[0]?.message?.content);
      return { success: true, analysis };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error analyzing ticket:', error);
      }
      return { success: false, error: errorInfo?.message };
    }
  },

  /**
   * Generate automated response for common issues
   */
  async generateAutomatedResponse(ticketSubject, ticketDescription, category) {
    try {
      const response = await openai?.chat?.completions?.create({
        model: 'gpt-5-mini',
        messages: [
          {
            role: 'system',
            content: `You are a helpful gym support assistant. Generate professional, empathetic responses to member inquiries. Provide clear, actionable solutions. If the issue requires staff intervention, acknowledge the ticket and inform them a team member will follow up.`
          },
          {
            role: 'user',
            content: `Category: ${category}\nSubject: ${ticketSubject}\nDescription: ${ticketDescription}\n\nGenerate a helpful response.`
          }
        ],
        reasoning_effort: 'medium',
        verbosity: 'medium'
      });

      return {
        success: true,
        response: response?.choices?.[0]?.message?.content
      };
    } catch (error) {
      const errorInfo = getErrorMessage(error);
      if (errorInfo?.isInternal) {
        console.log(errorInfo?.message);
      } else {
        console.error('Error generating response:', error);
      }
      return { success: false, error: errorInfo?.message };
    }
  },

  /**
   * Find similar resolved tickets
   */
  async findSimilarTickets(subject, description, tenantId, limit = 5) {
    try {
      const { data, error } = await supabase
        ?.from('support_tickets')
        ?.select('*')
        ?.eq('tenant_id', tenantId)
        ?.eq('status', 'resolved')
        ?.order('created_at', { ascending: false })
        ?.limit(limit);

      if (error) throw error;
      return { success: true, tickets: data || [] };
    } catch (error) {
      console.error('Error finding similar tickets:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Create support ticket
   */
  async createTicket(ticketData, userId, tenantId) {
    try {
      // Analyze ticket with AI
      const analysisResult = await this.analyzeTicket(
        ticketData?.subject,
        ticketData?.description
      );

      const analysis = analysisResult?.success ? analysisResult?.analysis : {
        priority: 'medium',
        category: 'general',
        sentiment: 'neutral',
        suggested_department: 'member_services',
        complexity_score: 5,
        requires_human: true,
        analysis_summary: 'Manual analysis required'
      };

      // Generate automated response if applicable
      let automatedResponse = null;
      if (!analysis?.requires_human && analysis?.complexity_score <= 5) {
        const responseResult = await this.generateAutomatedResponse(
          ticketData?.subject,
          ticketData?.description,
          analysis?.category
        );
        if (responseResult?.success) {
          automatedResponse = responseResult?.response;
        }
      }

      // Create ticket in database
      const { data: ticket, error } = await supabase
        ?.from('support_tickets')
        ?.insert({
          tenant_id: tenantId,
          user_id: userId,
          subject: ticketData?.subject,
          description: ticketData?.description,
          priority: analysis?.priority,
          category: analysis?.category,
          status: automatedResponse ? 'in_progress' : 'open',
          assigned_department: analysis?.suggested_department,
          ai_analysis: analysis,
          ai_confidence_score: (10 - analysis?.complexity_score) / 10
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Add automated response as first message if available
      if (automatedResponse && ticket?.id) {
        await supabase
          ?.from('ticket_messages')
          ?.insert({
            ticket_id: ticket?.id,
            sender_id: userId,
            sender_type: 'system',
            message: automatedResponse,
            is_automated: true
          });
      }

      return { success: true, ticket, automatedResponse };
    } catch (error) {
      console.error('Error creating ticket:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Get tickets for user or staff
   */
  async getTickets(userId, tenantId, isStaff = false, filters = {}) {
    try {
      let query = supabase
        ?.from('support_tickets')
        ?.select('*, user_profiles(full_name, email)')
        ?.eq('tenant_id', tenantId);

      if (!isStaff) {
        query = query?.eq('user_id', userId);
      }

      if (filters?.status) {
        query = query?.eq('status', filters?.status);
      }

      if (filters?.priority) {
        query = query?.eq('priority', filters?.priority);
      }

      if (filters?.category) {
        query = query?.eq('category', filters?.category);
      }

      const { data, error } = await query?.order('created_at', { ascending: false });

      if (error) throw error;
      return { success: true, tickets: data || [] };
    } catch (error) {
      console.error('Error fetching tickets:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Get ticket messages
   */
  async getTicketMessages(ticketId) {
    try {
      const { data, error } = await supabase
        ?.from('ticket_messages')
        ?.select('*, user_profiles(full_name)')
        ?.eq('ticket_id', ticketId)
        ?.order('created_at', { ascending: true });

      if (error) throw error;
      return { success: true, messages: data || [] };
    } catch (error) {
      console.error('Error fetching messages:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Add message to ticket
   */
  async addMessage(ticketId, senderId, message, senderType = 'member') {
    try {
      const { data, error } = await supabase
        ?.from('ticket_messages')
        ?.insert({
          ticket_id: ticketId,
          sender_id: senderId,
          sender_type: senderType,
          message: message
        })
        ?.select()
        ?.single();

      if (error) throw error;

      // Update ticket status if needed
      await supabase
        ?.from('support_tickets')
        ?.update({ 
          status: 'in_progress',
          updated_at: new Date()?.toISOString()
        })
        ?.eq('id', ticketId);

      return { success: true, message: data };
    } catch (error) {
      console.error('Error adding message:', error);
      return { success: false, error: error?.message };
    }
  },

  /**
   * Update ticket status
   */
  async updateTicketStatus(ticketId, status, assignedTo = null) {
    try {
      const updateData = { 
        status,
        updated_at: new Date()?.toISOString()
      };

      if (assignedTo) {
        updateData.assigned_to = assignedTo;
      }

      const { error } = await supabase
        ?.from('support_tickets')
        ?.update(updateData)
        ?.eq('id', ticketId);

      if (error) throw error;
      return { success: true };
    } catch (error) {
      console.error('Error updating ticket:', error);
      return { success: false, error: error?.message };
    }
  }
};

export default supportTicketService;